import { z } from "zod";
import type { infer as zInfer } from "zod/lib/types";

/**
 * The available data sources for user verification, the order here
 * also dictates the order they appear in the UI
 */
const VERIFICATION_DATA_SOURCES = {
  AUSTRALIAN_DRIVERS_LICENCE: "AustralianDriversLicence",
  AUSTRALIAN_PASSPORT: "AustralianPassport",
  MEDICARE_CARD: "MedicareCard",
  AUSTRALIAN_VISA: "AustralianVisaCheck",
  NEW_ZEALAND_DRIVERS_LICENCE: "NewZealandDriversLicence",
  NEW_ZEALAND_PASSPORT: "NewZealandPassport",
  NATIONAL_ID: "NationalId",
} as const;

/** The available data sources for user verification */
type VerificationDataSource =
  (typeof VERIFICATION_DATA_SOURCES)[keyof typeof VERIFICATION_DATA_SOURCES];

const VERIFICATION_STATUSES = {
  UNVERIFIED: "UNVERIFIED",
  IN_PROGRESS: "IN_PROGRESS",
  FAILED: "FAILED",
  VERIFIED: "VERIFIED",
  ERROR_OCCURRED: "ERROR_OCCURRED",
  VERIFIED_ADMIN: "VERIFIED_ADMIN",
  PENDING_REVIEW: "PENDING_REVIEW",
} as const;

/** The potential statuses for a user verification */
type VerificationStatus =
  (typeof VERIFICATION_STATUSES)[keyof typeof VERIFICATION_STATUSES];

const verificationDataSourceSchema = z.object({
  displayOrder: z.number().nullable(),
  name: z.string(),
  status: z.nativeEnum(VERIFICATION_STATUSES),
  sufficient: z.boolean(),
});

const userVerifications = z.object({
  id: z.string(),
  status: z.string(),
  dataSources: z.record(
    z.nativeEnum(VERIFICATION_DATA_SOURCES),
    verificationDataSourceSchema,
  ),
});

/** The object containing data for the respective verification data source */
type VerificationDataSourceSchema = zInfer<typeof verificationDataSourceSchema>;
type UserVerifications = zInfer<typeof userVerifications>;

export {
  userVerifications,
  VERIFICATION_STATUSES,
  VERIFICATION_DATA_SOURCES,
  type UserVerifications,
  type VerificationDataSourceSchema,
  VerificationDataSource,
  VerificationStatus,
};
